import { toast } from 'sonner'

/**
 * Titleize a single token (handle numbers and special cases)
 */
function titleizeToken(token: string): string {
  if (/^\d+(\.\d+)?$/.test(token)) return token
  if (token.toLowerCase() === 'oss') return 'OSS'
  return token.charAt(0).toUpperCase() + token.slice(1)
}

/**
 * Format a model ID into a readable display name
 * @example formatModelIdToName('claude-opus-4-6') → 'Claude Opus 4 6'
 */
export function formatModelIdToName(modelId: string): string {
  return modelId
    .replace(/[._]/g, '-')
    .split('-')
    .filter(Boolean)
    .map(titleizeToken)
    .join(' ')
}

/**
 * Derive a version string from a model ID
 * @example deriveVersionFromModelId('claude-opus-4-6') → '4-6'
 */
export function deriveVersionFromModelId(modelId: string): string {
  const parts = modelId.split('-').filter(Boolean)
  if (parts.length <= 1) return modelId

  const versionPart = parts.slice(1).join('-')
  if (/^[a-z]\d+(\.\d+)?$/i.test(versionPart)) {
    return versionPart.toUpperCase()
  }
  if (/^\d/.test(versionPart)) {
    return versionPart
  }

  return versionPart
    .split('-')
    .map((part) => {
      if (/^\d+(\.\d+)?$/.test(part)) return part
      if (part.toLowerCase() === 'oss') return 'OSS'
      return part.charAt(0).toUpperCase() + part.slice(1)
    })
    .join(' ')
}

/**
 * Format a price to 3 significant digits, never fewer than 2 decimals, so
 * tier-multiplied/discounted rates don't surface float artifacts
 * ("$0.041667") while genuine sub-cent prices keep their meaning.
 * @example formatPrice(1.234567) → '$1.23'
 * @example formatPrice(0.041667) → '$0.0417'
 * @example formatPrice(0.0028) → '$0.0028'
 */
export function formatPrice(price: number): string {
  const magnitude = price > 0 ? Math.floor(Math.log10(price)) : 0
  return `$${price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(2, 2 - magnitude),
  })}`
}

/**
 * Copy model ID to clipboard and show toast notification
 */
export async function copyModelId(modelId: string): Promise<void> {
  if (typeof window === 'undefined') return

  try {
    await navigator.clipboard.writeText(modelId)
    toast.success(`Copied ${modelId} to clipboard!`)
  } catch {
    // Clipboard API unavailable in this context
  }
}
